import React, { useState, useCallback, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Receipt as SalesImportIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import { useAuth } from '../../../../context/AuthContext';
import SalesImportDeprecationBanner from '../../../../components/venue/SalesImportDeprecationBanner';
import salesImportService from '../../../../services/salesImportService';
import type { SalesImport, SalesImportStatus } from '../../../../types/salesImport';

const formatDate = (d: string | null | undefined): string => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const STATUS_LABEL: Record<SalesImportStatus, string> = {
  draft: 'Draft',
  posted: 'Posted',
  failed: 'Failed',
};

const STATUS_COLOR: Record<SalesImportStatus, 'default' | 'success' | 'error'> = {
  draft: 'default',
  posted: 'success',
  failed: 'error',
};

const shortId = (id: string): string => (id.length > 8 ? id.slice(-8) : id);

const SalesImportList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { venueId } = useVenue();
  const { isAdmin } = useAuth();
  const admin = isAdmin();
  const base = `/admin/venues/${venueId}/sales-imports`;

  const [items, setItems] = useState<SalesImport[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<SalesImportStatus | 'all'>('all');
  // Global counts keyed by status — derived from cheap totalItems-only fetches
  // (pageSize=1) so the header strip reflects the whole venue, not the current
  // page. Uses existing list endpoint only; no backend changes.
  const [counts, setCounts] = useState<{ draft: number; posted: number; failed: number; all: number }>({
    draft: 0,
    posted: 0,
    failed: 0,
    all: 0,
  });

  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await salesImportService.list(
        venueId,
        page + 1,
        pageSize,
        statusFilter === 'all' ? undefined : statusFilter,
      );
      setItems(result.data);
      setTotal(result.pagination.totalItems);
    } catch (err: any) {
      setError(
        err?.response?.data?.error?.message ??
          err?.response?.data?.message ??
          err?.message ??
          'Failed to load sales imports',
      );
    } finally {
      setLoading(false);
    }
  }, [venueId, page, pageSize, statusFilter]);

  const loadCounts = useCallback(async () => {
    if (!venueId) return;
    try {
      const [d, p, f, a] = await Promise.all([
        salesImportService.list(venueId, 1, 1, 'draft'),
        salesImportService.list(venueId, 1, 1, 'posted'),
        salesImportService.list(venueId, 1, 1, 'failed'),
        salesImportService.list(venueId, 1, 1),
      ]);
      setCounts({
        draft: d.pagination.totalItems,
        posted: p.pagination.totalItems,
        failed: f.pagination.totalItems,
        all: a.pagination.totalItems,
      });
    } catch {
      // Counts are a nice-to-have; failure does not block the list.
    }
  }, [venueId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  const handleDelete = async (importId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Delete this draft sales import? This cannot be undone.')) return;
    setDeleting(importId);
    try {
      await salesImportService.remove(venueId, importId);
      load();
      loadCounts();
    } catch (err: any) {
      setError(
        err?.response?.data?.error?.message ??
          err?.response?.data?.message ??
          err?.message ??
          'Failed to delete sales import',
      );
    } finally {
      setDeleting(null);
    }
  };

  const handleFilterChange = (_e: React.MouseEvent<HTMLElement>, next: SalesImportStatus | 'all' | null) => {
    if (next === null) return; // ToggleButtonGroup emits null when user clicks the active button
    setStatusFilter(next);
    setPage(0);
  };

  // Quick-count strip configuration. Tones drive the accent colour when count > 0.
  type CountKey = SalesImportStatus | 'all';
  const countCards: Array<{
    key: CountKey;
    label: string;
    value: number;
    tone?: 'success' | 'warning' | 'error';
  }> = ([
    { key: 'all', label: 'Total' },
    { key: 'draft', label: 'Draft' },
    { key: 'posted', label: 'Posted', tone: 'success' as const },
    { key: 'failed', label: 'Failed', tone: 'error' as const },
  ] as const).map((c) => ({ ...c, value: counts[c.key] }));

  return (
    <Box sx={{ p: 3 }}>
      <SalesImportDeprecationBanner isAdmin={admin} sx={{ mb: 3 }} />
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box
          sx={{
            px: 3,
            py: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SalesImportIcon sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" fontWeight={600}>
              Sales Imports
            </Typography>
          </Box>
          {admin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(`${base}/import`)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Import Sales
            </Button>
          )}
        </Box>

        {/* Quick-counts strip + status filter */}
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          }}
        >
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            {countCards.map((c) => {
              const tone =
                c.tone === 'error'
                  ? theme.palette.error.main
                  : c.tone === 'warning'
                    ? theme.palette.warning.main
                    : c.tone === 'success'
                      ? theme.palette.success.main
                      : theme.palette.text.primary;
              const accentOn = !!c.tone && c.value > 0;
              return (
                <Paper
                  key={c.key}
                  variant="outlined"
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    minWidth: 96,
                    borderRadius: 2,
                    borderColor: accentOn ? alpha(tone, 0.4) : alpha(theme.palette.divider, 0.5),
                    bgcolor: accentOn ? alpha(tone, 0.05) : 'transparent',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ textTransform: 'uppercase', color: 'text.secondary', fontWeight: 600, fontSize: '0.65rem' }}
                  >
                    {c.label}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      lineHeight: 1.2,
                      color: accentOn ? tone : 'text.primary',
                    }}
                  >
                    {c.value}
                  </Typography>
                </Paper>
              );
            })}
          </Stack>

          <ToggleButtonGroup
            value={statusFilter}
            exclusive
            size="small"
            onChange={handleFilterChange}
            sx={{ alignSelf: { xs: 'stretch', md: 'auto' } }}
          >
            <ToggleButton value="all" sx={{ textTransform: 'none', px: 2 }}>All</ToggleButton>
            <ToggleButton value="draft" sx={{ textTransform: 'none', px: 2 }}>Draft</ToggleButton>
            <ToggleButton value="posted" sx={{ textTransform: 'none', px: 2 }}>Posted</ToggleButton>
            <ToggleButton value="failed" sx={{ textTransform: 'none', px: 2 }}>Failed</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {error && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Box>
        )}

        {loading ? (
          <Box sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Loading sales imports…
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {[
                      'Import ID',
                      'Status',
                      'Source File',
                      'Created',
                      'Posted',
                      'Rows',
                      'Posted',
                      'Failed',
                      '',
                    ].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.text.secondary,
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Box sx={{ py: 8, textAlign: 'center', color: theme.palette.text.secondary }}>
                          <SalesImportIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
                          <Typography variant="body2">
                            No sales imports for this venue. Sales are now recorded automatically
                            through the POS integration.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((imp) => (
                      <TableRow
                        key={imp._id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`${base}/${imp._id}`)}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={500} sx={{ fontFamily: 'monospace' }}>
                            …{shortId(imp._id)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={STATUS_LABEL[imp.status]}
                            size="small"
                            color={STATUS_COLOR[imp.status]}
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {imp.sourceFilename || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatDate(imp.createdAt)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(imp.postedAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{imp.rowCount}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{imp.postedLineCount}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              color: imp.failedLineCount > 0
                                ? theme.palette.error.main
                                : theme.palette.text.secondary,
                              fontWeight: imp.failedLineCount > 0 ? 600 : 400,
                            }}
                          >
                            {imp.failedLineCount}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {imp.status === 'draft' && (
                            <Tooltip title="Delete draft">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleDelete(imp._id, e)}
                                  disabled={deleting === imp._id}
                                  sx={{ color: theme.palette.text.disabled }}
                                >
                                  {deleting === imp._id ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <DeleteIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              rowsPerPage={pageSize}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={(e) => {
                setPageSize(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default SalesImportList;
